/* iter69 / iter69.1 — Production Continuity panel.
 *
 * Pre-Storyboard / pre-Prompt-Pack reference lock UI.
 * Reference image binding uses upload + project-asset picker
 * (iter69.1) — no more pasted asset IDs in normal flow.
 *
 * Backward compatible: when no lock exists the panel renders an
 * empty state with Add CTAs. Warning-based gate only — never
 * hard-blocks Storyboard.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Users, MapPin, Tag, ShieldCheck, ShieldAlert,
    Plus, CheckCircle2, X as XIcon, RefreshCw, Loader2,
    Upload, Image as ImageIcon, Search,
} from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
// Reference Image Preview / Lightbox Fix — shared full-size preview modal.
import ImageLightbox from "@/components/common/ImageLightbox";

// iter69.2 — CRA codebase: use the api.js `BACKEND_URL` export.
// `import.meta.env.*` is a Vite-only construct and will throw at runtime
// in our Create-React-App build, crashing the entire Production
// Continuity tab. The leading-slash trim keeps the join `${BACKEND}/api/...`
// safe even if the env value ever ends with a trailing slash.
const BACKEND = (BACKEND_URL || "").replace(/\/$/, "");

const APPROVAL_PILLS = {
    approved: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    rejected: "border-red-500/40 text-red-300 bg-red-500/10",
    draft:    "border-zinc-700 text-zinc-400 bg-zinc-900",
};

const ROLE_OPTIONS = [
    { v: "logo", label: "Logo" },
    { v: "product", label: "Product" },
    { v: "location", label: "Location" },
    { v: "founder_team", label: "Founder / team" },
    { v: "dashboard_screenshot", label: "Dashboard screenshot" },
    { v: "style_reference", label: "Style reference" },
    { v: "ad_reference", label: "Ad reference" },
    { v: "other", label: "Other" },
];

const CHAR_FIELDS = [
    { k: "name", label: "Name" },
    { k: "role", label: "Role / persona" },
    { k: "age_range", label: "Age range" },
    { k: "face_structure", label: "Face structure" },
    { k: "hair", label: "Hair" },
    { k: "beard_moustache", label: "Beard / moustache" },
    { k: "skin_tone", label: "Skin tone" },
    { k: "body_height_impression", label: "Body / height impression" },
    { k: "wardrobe", label: "Wardrobe" },
    { k: "accessories", label: "Accessories" },
    { k: "personality", label: "Personality" },
    { k: "emotional_tone", label: "Emotional tone" },
];

const ENV_FIELDS_STR = [
    { k: "name", label: "Name" },
    { k: "location_type", label: "Location type" },
    { k: "visual_mood", label: "Visual mood" },
    { k: "lighting", label: "Lighting" },
    { k: "time_of_day", label: "Time of day" },
    { k: "colour_palette", label: "Colour palette" },
    { k: "camera_friendly_notes", label: "Camera-friendly notes" },
];

const ENV_FIELDS_LIST = [
    { k: "furniture", label: "Furniture" },
    { k: "props", label: "Props" },
    { k: "background_details", label: "Background details" },
    { k: "brand_elements", label: "Brand elements" },
];

function ApprovalPill({ status }) {
    const cls = APPROVAL_PILLS[status] || APPROVAL_PILLS.draft;
    return (
        <span
            className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}
            data-testid={`continuity-approval-pill-${status || "draft"}`}
        >
            {status || "draft"}
        </span>
    );
}

function assetThumbUrl(asset) {
    if (!asset) return "";
    if (asset.external_url) return asset.external_url;
    if (asset.storage_path) {
        // iter69.2 — `<img>` cannot send Authorization headers, so we
        // forward the JWT via the `?auth=<token>` query param. The
        // backend route `GET /api/assets/{id}/stream` accepts both the
        // header form (axios) and the query form (HTML media tags) via
        // `get_user_header_or_query`. URL-encoding protects against any
        // future tokens that contain `=` / `+` / `&`.
        const token = (typeof window !== "undefined")
            ? localStorage.getItem("aivos_token") : null;
        const qs = token ? `?auth=${encodeURIComponent(token)}` : "";
        return `${BACKEND}/api/assets/${asset.id}/stream${qs}`;
    }
    return "";
}

// ============================================================
// AssetPickerModal — list project + client-scoped assets,
// support filter by usage_tag, single-select.
// ============================================================
function AssetPickerModal({ projectId, onClose, onPicked, scope }) {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/assets?project_id=${encodeURIComponent(projectId)}`);
                setAssets(data || []);
            } catch {
                toast.error("Could not load project assets");
            } finally {
                setLoading(false);
            }
        })();
    }, [projectId]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (assets || []).filter((a) => {
            if (!q) return true;
            return (
                (a.original_filename || "").toLowerCase().includes(q)
                || (a.notes || "").toLowerCase().includes(q)
                || (a.usage_tags || []).join(" ").toLowerCase().includes(q)
                || (a.id || "").toLowerCase().includes(q)
            );
        });
    }, [assets, query]);

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            data-testid="continuity-asset-picker-modal"
        >
            <div className="card-base bg-zinc-950 max-w-3xl w-full max-h-[80vh] flex flex-col">
                <div className="flex items-center gap-2 p-4 border-b border-zinc-800">
                    <Search className="w-4 h-4 text-zinc-500" />
                    <input
                        type="search"
                        placeholder="Filter by filename / tag / id…"
                        className="input-base flex-1 text-sm"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="continuity-asset-picker-filter"
                    />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {filtered.length} / {assets.length}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary text-xs"
                        data-testid="continuity-asset-picker-close"
                    >
                        <XIcon className="w-3.5 h-3.5" /> Close
                    </button>
                </div>
                <div className="p-3 overflow-y-auto flex-1">
                    {loading && <p className="text-xs text-zinc-500">Loading…</p>}
                    {!loading && filtered.length === 0 && (
                        <p className="text-xs text-zinc-500 italic">
                            No project assets match. Upload a new one from the
                            character / environment editor instead.
                        </p>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                        {filtered.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => onPicked(a)}
                                className="border border-zinc-800 rounded-sm bg-zinc-900 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition p-2 text-left"
                                data-testid={`continuity-asset-pick-${a.id}`}
                            >
                                {(a.asset_type === "image" || a.content_type?.startsWith("image/")) ? (
                                    <img
                                        src={assetThumbUrl(a)}
                                        alt={a.original_filename || a.id}
                                        className="w-full h-24 object-contain rounded-sm bg-zinc-950"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="w-full h-24 flex items-center justify-center bg-zinc-800 rounded-sm">
                                        <ImageIcon className="w-6 h-6 text-zinc-600" />
                                    </div>
                                )}
                                <div className="mt-1.5">
                                    <p className="text-[11px] text-zinc-200 truncate">
                                        {a.original_filename || "(unnamed)"}
                                    </p>
                                    <p className="text-[10px] font-mono text-zinc-500 truncate">
                                        {a.id?.slice(0, 12)}…
                                    </p>
                                    {(a.usage_tags || []).slice(0, 2).map((t) => (
                                        <span
                                            key={t}
                                            className="inline-block text-[9px] font-mono uppercase tracking-wider text-zinc-400 bg-zinc-800 border border-zinc-700 px-1 py-0.5 rounded-sm mr-1 mt-0.5"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// ReferenceImageBinder — upload OR pick OR (advanced) paste id.
// Used by character + environment editors.
// ============================================================
function ReferenceImageBinder({
    projectId, value, onChange, usageContext,
    testIdPrefix, onOpenAsset,
}) {
    const [showPicker, setShowPicker] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [details, setDetails] = useState({});      // {asset_id: assetRow}
    const fileRef = useRef(null);

    const ids = value || [];

    // Hydrate thumbnails for already-bound asset ids.
    useEffect(() => {
        if (!ids.length || !projectId) return;
        (async () => {
            try {
                const { data } = await api.get(`/assets?project_id=${encodeURIComponent(projectId)}`);
                const map = {};
                for (const a of data || []) map[a.id] = a;
                setDetails((prev) => ({ ...prev, ...map }));
            } catch {
                /* ignore */
            }
        })();
    }, [projectId, ids.join(",")]);

    const uploadOne = async (file) => {
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("project_id", projectId);
            form.append("asset_type", "image");
            form.append("usage_tags", `production_continuity,${usageContext}`);
            const { data } = await api.post(`/uploads`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDetails((prev) => ({ ...prev, [data.id]: data }));
            onChange([...ids, data.id]);
            toast.success("Reference image uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onPicked = (asset) => {
        if (ids.includes(asset.id)) {
            toast.message("Already bound");
        } else {
            setDetails((prev) => ({ ...prev, [asset.id]: asset }));
            onChange([...ids, asset.id]);
        }
        setShowPicker(false);
    };

    const unbind = (assetId) => {
        onChange(ids.filter((x) => x !== assetId));
    };

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-binder`}>
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadOne(f);
                        e.target.value = "";
                    }}
                    data-testid={`${testIdPrefix}-upload-input`}
                />
                <button
                    type="button"
                    className="btn-primary text-[11px]"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    {uploading
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Upload className="w-3 h-3" />}
                    Upload reference image
                </button>
                <button
                    type="button"
                    className="btn-secondary text-[11px]"
                    onClick={() => setShowPicker(true)}
                    data-testid={`${testIdPrefix}-pick-btn`}
                >
                    <ImageIcon className="w-3 h-3" /> Select from project assets
                </button>
                <button
                    type="button"
                    className="text-[10px] text-zinc-500 underline hover:text-zinc-300 ml-auto"
                    onClick={() => setShowAdvanced((s) => !s)}
                    data-testid={`${testIdPrefix}-advanced-toggle`}
                >
                    {showAdvanced ? "Hide" : "Advanced"}: paste IDs
                </button>
            </div>

            {ids.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                    {ids.map((aid) => {
                        const a = details[aid];
                        return (
                            <div
                                key={aid}
                                className="relative border border-zinc-800 rounded-sm bg-zinc-900 p-1"
                                data-testid={`${testIdPrefix}-thumb-${aid}`}
                            >
                                {a && (a.asset_type === "image" || a.content_type?.startsWith("image/")) ? (
                                    typeof onOpenAsset === "function" ? (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenAsset({ assetId: aid, title: a.original_filename || aid });
                                            }}
                                            data-testid={`${testIdPrefix}-thumb-open-${aid}`}
                                            title="Click to preview full image"
                                            aria-label={`Preview ${a.original_filename || aid}`}
                                            className="w-full p-0 m-0 border-0 bg-transparent cursor-zoom-in rounded-sm overflow-hidden hover:ring-1 hover:ring-emerald-500/40 transition"
                                        >
                                            <img
                                                src={assetThumbUrl(a)}
                                                alt={a.original_filename || aid}
                                                className="w-full h-20 object-contain rounded-sm bg-zinc-950"
                                                loading="lazy"
                                            />
                                        </button>
                                    ) : (
                                        <img
                                            src={assetThumbUrl(a)}
                                            alt={a.original_filename || aid}
                                            className="w-full h-20 object-contain rounded-sm bg-zinc-950"
                                            loading="lazy"
                                        />
                                    )
                                ) : (
                                    <div className="w-full h-20 flex items-center justify-center bg-zinc-800 rounded-sm">
                                        <ImageIcon className="w-5 h-5 text-zinc-600" />
                                    </div>
                                )}
                                <p className="text-[9px] font-mono text-zinc-400 truncate mt-1">
                                    {a?.original_filename || aid.slice(0, 12)}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => unbind(aid)}
                                    className="absolute -top-1.5 -right-1.5 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 hover:bg-red-500/20 hover:border-red-500"
                                    data-testid={`${testIdPrefix}-unbind-${aid}`}
                                    title="Unbind"
                                >
                                    <XIcon className="w-3 h-3 text-zinc-400" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {showAdvanced && (
                <label className="text-[11px] text-zinc-500 block">
                    Reference asset IDs (comma-separated, advanced)
                    <input
                        className="input-base w-full text-xs mt-1 font-mono"
                        value={ids.join(",")}
                        onChange={(e) => onChange(
                            e.target.value.split(",")
                                .map((s) => s.trim()).filter(Boolean),
                        )}
                        data-testid={`${testIdPrefix}-advanced-input`}
                    />
                </label>
            )}

            {showPicker && (
                <AssetPickerModal
                    projectId={projectId}
                    onClose={() => setShowPicker(false)}
                    onPicked={onPicked}
                />
            )}
        </div>
    );
}

function CharacterEditor({ projectId, initial, onSaved, onCancel, onOpenAsset }) {
    const [form, setForm] = useState({
        name: "", role: "", age_range: "", face_structure: "", hair: "",
        beard_moustache: "", skin_tone: "", body_height_impression: "",
        wardrobe: "", accessories: "", personality: "", emotional_tone: "",
        do_not_change_rules: [],
        reference_asset_ids: [],
        ...initial,
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }
        setBusy(true);
        try {
            const url = initial?.id
                ? `/projects/${projectId}/production-continuity/characters/${initial.id}`
                : `/projects/${projectId}/production-continuity/characters`;
            const method = initial?.id ? "patch" : "post";
            const { data } = await api[method](url, form);
            toast.success(initial?.id ? "Character updated" : "Character created");
            onSaved?.(data.character);
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.error === "continuity_asset_not_in_project") {
                toast.error(`Asset not in project: ${(d.missing || []).join(", ")}`);
            } else {
                toast.error(typeof d === "string" ? d : "Save failed");
            }
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            className="border border-zinc-800 rounded-sm bg-zinc-950 p-3 space-y-2"
            data-testid="continuity-character-editor"
        >
            <div className="grid grid-cols-2 gap-2">
                {CHAR_FIELDS.map(({ k, label }) => (
                    <label key={k} className="text-[11px] text-zinc-400 block">
                        {label}
                        <input
                            className="input-base w-full text-xs mt-1"
                            value={form[k] || ""}
                            onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                            data-testid={`continuity-char-input-${k}`}
                        />
                    </label>
                ))}
            </div>
            <label className="text-[11px] text-zinc-400 block">
                Do-not-change rules (one per line)
                <textarea
                    className="input-base w-full text-xs mt-1"
                    rows={2}
                    value={(form.do_not_change_rules || []).join("\n")}
                    onChange={(e) => setForm((f) => ({
                        ...f,
                        do_not_change_rules: e.target.value.split("\n").filter(Boolean),
                    }))}
                    data-testid="continuity-char-input-do_not_change_rules"
                />
            </label>

            <div className="border-t border-zinc-800 pt-2">
                <div className="flex items-center gap-2 mb-1">
                    <ImageIcon className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="text-[11px] text-zinc-300 font-display">
                        Character reference images
                    </span>
                </div>
                <ReferenceImageBinder
                    projectId={projectId}
                    value={form.reference_asset_ids}
                    onChange={(ids) => setForm((f) => ({ ...f, reference_asset_ids: ids }))}
                    usageContext="character_reference"
                    testIdPrefix="continuity-char-ref"
                    onOpenAsset={onOpenAsset}
                />
                <p className="text-[10px] text-zinc-500 italic mt-1">
                    Tip: upload a hero portrait + wardrobe shot. AI reference
                    generation coming later.
                </p>
            </div>

            <div className="flex gap-2 pt-1">
                <button
                    type="button"
                    className="btn-primary text-[11px]"
                    onClick={submit}
                    disabled={busy}
                    data-testid="continuity-char-save"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    {initial?.id ? "Save changes" : "Create character"}
                </button>
                <button
                    type="button"
                    className="btn-secondary text-[11px]"
                    onClick={onCancel}
                    data-testid="continuity-char-cancel"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function EnvironmentEditor({ projectId, initial, onSaved, onCancel, onOpenAsset }) {
    const [form, setForm] = useState({
        name: "", location_type: "", visual_mood: "", lighting: "",
        time_of_day: "", colour_palette: "", camera_friendly_notes: "",
        furniture: [], props: [], background_details: [], brand_elements: [],
        do_not_change_rules: [],
        reference_asset_ids: [],
        ...initial,
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }
        setBusy(true);
        try {
            const url = initial?.id
                ? `/projects/${projectId}/production-continuity/environments/${initial.id}`
                : `/projects/${projectId}/production-continuity/environments`;
            const method = initial?.id ? "patch" : "post";
            const { data } = await api[method](url, form);
            toast.success(initial?.id ? "Environment updated" : "Environment created");
            onSaved?.(data.environment);
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.error === "continuity_asset_not_in_project") {
                toast.error(`Asset not in project: ${(d.missing || []).join(", ")}`);
            } else {
                toast.error(typeof d === "string" ? d : "Save failed");
            }
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            className="border border-zinc-800 rounded-sm bg-zinc-950 p-3 space-y-2"
            data-testid="continuity-env-editor"
        >
            <div className="grid grid-cols-2 gap-2">
                {ENV_FIELDS_STR.map(({ k, label }) => (
                    <label key={k} className="text-[11px] text-zinc-400 block">
                        {label}
                        <input
                            className="input-base w-full text-xs mt-1"
                            value={form[k] || ""}
                            onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                            data-testid={`continuity-env-input-${k}`}
                        />
                    </label>
                ))}
            </div>
            {ENV_FIELDS_LIST.map(({ k, label }) => (
                <label key={k} className="text-[11px] text-zinc-400 block">
                    {label} (one per line)
                    <textarea
                        className="input-base w-full text-xs mt-1"
                        rows={2}
                        value={(form[k] || []).join("\n")}
                        onChange={(e) => setForm((f) => ({
                            ...f,
                            [k]: e.target.value.split("\n").filter(Boolean),
                        }))}
                        data-testid={`continuity-env-input-${k}`}
                    />
                </label>
            ))}
            <label className="text-[11px] text-zinc-400 block">
                Do-not-change rules (one per line)
                <textarea
                    className="input-base w-full text-xs mt-1"
                    rows={2}
                    value={(form.do_not_change_rules || []).join("\n")}
                    onChange={(e) => setForm((f) => ({
                        ...f,
                        do_not_change_rules: e.target.value.split("\n").filter(Boolean),
                    }))}
                />
            </label>

            <div className="border-t border-zinc-800 pt-2">
                <div className="flex items-center gap-2 mb-1">
                    <ImageIcon className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="text-[11px] text-zinc-300 font-display">
                        Environment reference images
                    </span>
                </div>
                <ReferenceImageBinder
                    projectId={projectId}
                    value={form.reference_asset_ids}
                    onChange={(ids) => setForm((f) => ({ ...f, reference_asset_ids: ids }))}
                    usageContext="environment_reference"
                    testIdPrefix="continuity-env-ref"
                    onOpenAsset={onOpenAsset}
                />
                <p className="text-[10px] text-zinc-500 italic mt-1">
                    Tip: upload a wide angle of the location plus one prop /
                    background detail close-up.
                </p>
            </div>

            <div className="flex gap-2 pt-1">
                <button
                    type="button"
                    className="btn-primary text-[11px]"
                    onClick={submit}
                    disabled={busy}
                    data-testid="continuity-env-save"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    {initial?.id ? "Save changes" : "Create environment"}
                </button>
                <button
                    type="button"
                    className="btn-secondary text-[11px]"
                    onClick={onCancel}
                    data-testid="continuity-env-cancel"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function ReferenceLockBinder({ projectId, lock, onChange, onOpenAsset }) {
    const [showPicker, setShowPicker] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [pendingId, setPendingId] = useState("");
    const [uploading, setUploading] = useState(false);
    const [details, setDetails] = useState({});
    const [pendingRole, setPendingRole] = useState("logo");
    const fileRef = useRef(null);

    const approved = lock?.reference_lock?.approved_asset_ids || [];
    const roles = lock?.reference_lock?.roles || {};

    // Hydrate thumbnails for bound asset ids.
    useEffect(() => {
        if (!approved.length || !projectId) return;
        (async () => {
            try {
                const { data } = await api.get(`/assets?project_id=${encodeURIComponent(projectId)}`);
                const map = {};
                for (const a of data || []) map[a.id] = a;
                setDetails((prev) => ({ ...prev, ...map }));
            } catch { /* ignore */ }
        })();
    }, [projectId, approved.join(",")]);

    const bindIds = async (ids, role) => {
        if (!ids.length) return;
        try {
            const roleMap = {};
            for (const aid of ids) roleMap[aid] = role || pendingRole || "other";
            await api.post(`/projects/${projectId}/production-continuity/reference-lock/bind`, {
                asset_ids: ids,
                roles: roleMap,
            });
            await onChange();
            toast.success(`Bound ${ids.length} asset(s) as ${role || pendingRole}`);
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.error === "continuity_asset_not_in_project") {
                toast.error(`Asset not in project: ${(d.missing || []).join(", ")}`);
            } else {
                toast.error("Bind failed");
            }
        }
    };

    const uploadAndBind = async (file) => {
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("project_id", projectId);
            form.append("asset_type", "image");
            form.append("usage_tags",
                `production_continuity,brand_reference,${pendingRole}`);
            const { data } = await api.post(`/uploads`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDetails((prev) => ({ ...prev, [data.id]: data }));
            await bindIds([data.id], pendingRole);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onPicked = (asset) => {
        setDetails((prev) => ({ ...prev, [asset.id]: asset }));
        setShowPicker(false);
        bindIds([asset.id], pendingRole);
    };

    const unbind = async (aid) => {
        try {
            await api.delete(`/projects/${projectId}/production-continuity/reference-lock/${aid}`);
            await onChange();
        } catch {
            toast.error("Unbind failed");
        }
    };

    return (
        <div data-testid="continuity-reference-lock">
            <div className="flex items-center gap-2 mb-2">
                <Tag className="w-4 h-4 text-zinc-500" />
                <h4 className="font-display text-sm">Brand / Reference Asset Lock</h4>
                <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                    {approved.length} bound
                </span>
            </div>

            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <label className="text-[11px] text-zinc-400">
                    Role for next bind:
                    <select
                        value={pendingRole}
                        onChange={(e) => setPendingRole(e.target.value)}
                        className="input-base text-xs ml-1 inline-block w-auto"
                        data-testid="continuity-reflock-role-select"
                    >
                        {ROLE_OPTIONS.map((r) => (
                            <option key={r.v} value={r.v}>{r.label}</option>
                        ))}
                    </select>
                </label>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadAndBind(f);
                        e.target.value = "";
                    }}
                    data-testid="continuity-reflock-upload-input"
                />
                <button
                    type="button"
                    className="btn-primary text-[11px]"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="continuity-reflock-upload-btn"
                >
                    {uploading
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Upload className="w-3 h-3" />}
                    Upload & bind
                </button>
                <button
                    type="button"
                    className="btn-secondary text-[11px]"
                    onClick={() => setShowPicker(true)}
                    data-testid="continuity-reflock-pick-btn"
                >
                    <ImageIcon className="w-3 h-3" /> Select from project assets
                </button>
                <button
                    type="button"
                    className="text-[10px] text-zinc-500 underline hover:text-zinc-300 ml-auto"
                    onClick={() => setShowAdvanced((s) => !s)}
                    data-testid="continuity-reflock-advanced-toggle"
                >
                    {showAdvanced ? "Hide" : "Advanced"}: paste IDs
                </button>
            </div>

            {showAdvanced && (
                <div className="flex gap-2 mb-2">
                    <input
                        className="input-base flex-1 text-xs font-mono"
                        placeholder="Paste asset_id(s), comma-separated…"
                        value={pendingId}
                        onChange={(e) => setPendingId(e.target.value)}
                        data-testid="continuity-reflock-input"
                    />
                    <button
                        type="button"
                        className="btn-primary text-[11px]"
                        onClick={async () => {
                            const ids = pendingId.split(",")
                                .map((s) => s.trim()).filter(Boolean);
                            if (ids.length) {
                                await bindIds(ids, pendingRole);
                                setPendingId("");
                            }
                        }}
                        disabled={!pendingId.trim()}
                        data-testid="continuity-reflock-bind"
                    >
                        <Plus className="w-3 h-3" /> Bind by ID
                    </button>
                </div>
            )}

            {approved.length === 0 ? (
                <p className="text-[11px] text-zinc-500 italic">
                    No reference assets bound yet. Upload or select logo /
                    product / location references to ground Storyboard visuals.
                </p>
            ) : (
                <div className="grid grid-cols-4 gap-2">
                    {approved.map((aid) => {
                        const a = details[aid];
                        return (
                            <div
                                key={aid}
                                className="relative border border-zinc-800 rounded-sm bg-zinc-900 p-1"
                                data-testid={`continuity-reflock-row-${aid}`}
                            >
                                {a && (a.asset_type === "image" || a.content_type?.startsWith("image/")) ? (
                                    typeof onOpenAsset === "function" ? (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenAsset({ assetId: aid, title: a.original_filename || aid });
                                            }}
                                            data-testid={`continuity-reflock-thumb-open-${aid}`}
                                            title="Click to preview full image"
                                            aria-label={`Preview ${a.original_filename || aid}`}
                                            className="w-full p-0 m-0 border-0 bg-transparent cursor-zoom-in rounded-sm overflow-hidden hover:ring-1 hover:ring-emerald-500/40 transition"
                                        >
                                            <img
                                                src={assetThumbUrl(a)}
                                                alt={a.original_filename || aid}
                                                className="w-full h-20 object-contain rounded-sm bg-zinc-950"
                                                loading="lazy"
                                            />
                                        </button>
                                    ) : (
                                        <img
                                            src={assetThumbUrl(a)}
                                            alt={a.original_filename || aid}
                                            className="w-full h-20 object-contain rounded-sm bg-zinc-950"
                                            loading="lazy"
                                        />
                                    )
                                ) : (
                                    <div className="w-full h-20 flex items-center justify-center bg-zinc-800 rounded-sm">
                                        <ImageIcon className="w-5 h-5 text-zinc-600" />
                                    </div>
                                )}
                                <p className="text-[9px] font-mono text-zinc-400 truncate mt-1">
                                    {a?.original_filename || aid.slice(0, 12)}
                                </p>
                                <span className="inline-block text-[9px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-1 py-0.5 rounded-sm mt-0.5">
                                    {roles[aid] || "other"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => unbind(aid)}
                                    className="absolute -top-1.5 -right-1.5 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 hover:bg-red-500/20 hover:border-red-500"
                                    data-testid={`continuity-reflock-unbind-${aid}`}
                                    title="Unbind"
                                >
                                    <XIcon className="w-3 h-3 text-zinc-400" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {showPicker && (
                <AssetPickerModal
                    projectId={projectId}
                    onClose={() => setShowPicker(false)}
                    onPicked={onPicked}
                />
            )}
        </div>
    );
}

export default function ProductionContinuityPanel({ projectId }) {
    const [lock, setLock] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [charEditor, setCharEditor] = useState(null);
    const [envEditor, setEnvEditor] = useState(null);
    // Sprint V1 — panel-scope asset hydration so Approve buttons can
    // gate on whether a bound ref_asset_id actually resolves to a
    // valid image. Empty fallback is safe — disabled-button + error
    // toast cover the failure path.
    const [assetById, setAssetById] = useState({});
    // Sprint V2 — single in-flight generation guard + once-per-session
    // confirmation flag. Both are component-scope, never persisted, so
    // they reset on page reload — exactly the credit-protection
    // behavior the user requested.
    const [generatingFor, setGeneratingFor] = useState(null);
    const [aiCreditsConfirmed, setAiCreditsConfirmed] = useState(false);
    const [pendingGenerate, setPendingGenerate] = useState(null);
    // Reference Image Preview / Lightbox Fix — single shared lightbox.
    const [lightbox, setLightbox] = useState({ open: false, assetId: null, title: "" });
    const openAsset = useCallback(({ assetId, title }) => {
        if (!assetId) return;
        setLightbox({ open: true, assetId, title: title || "" });
    }, []);
    const closeLightbox = useCallback(() => {
        setLightbox((prev) => ({ ...prev, open: false }));
    }, []);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const [{ data: contData }, assetRes] = await Promise.all([
                api.get(`/projects/${projectId}/production-continuity`),
                api.get(`/assets?project_id=${encodeURIComponent(projectId)}`)
                   .catch(() => ({ data: [] })),
            ]);
            setLock(contData.lock);
            setReadiness(contData.readiness);
            const map = {};
            for (const a of (assetRes.data || [])) {
                if (a && a.id) map[a.id] = a;
            }
            setAssetById(map);
        } catch (e) {
            toast.error("Could not load continuity lock");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    // Sprint V1 / V1.1 — pure predicate. True iff at least one of the
    // bound reference_asset_ids resolves to a valid, fetchable image in
    // the hydrated assetById map. Mirrors backend `_is_valid_visual_asset`.
    // V1.1: `content_type` is authoritative when present; `asset_type`
    // alone (image/logo) cannot prove validity if content_type is
    // non-image.
    const hasValidVisual = useCallback((refs) => {
        const ids = refs || [];
        const safeSuffix = /\.(png|jpe?g|webp|gif|svg|heic|heif|avif|bmp)$/i;
        for (const aid of ids) {
            const a = assetById[aid];
            if (!a) continue;
            if (a.is_deleted) continue;
            const storagePath = (a.storage_path || "").trim();
            const externalUrl = (a.external_url || "").trim();
            const filename = (a.original_filename || "").trim();
            const hasPath = !!storagePath || !!externalUrl;
            if (!hasPath) continue;
            if (a.source === "url" && a.reachable === false) continue;
            const ct = (a.content_type || "").toLowerCase().trim();
            if (ct) {
                // content_type is authoritative — non-image rejects
                // even if asset_type is image/logo.
                if (ct.startsWith("image/")) return true;
                continue;
            }
            // No content_type — fall back to safe filename/URL ext.
            for (const src of [storagePath, externalUrl, filename]) {
                if (!src) continue;
                const clean = src.split("?")[0];
                if (safeSuffix.test(clean)) return true;
            }
        }
        return false;
    }, [assetById]);

    const approve = async (kind, id, status) => {
        try {
            await api.post(
                `/projects/${projectId}/production-continuity/${kind}s/${id}/approval`,
                { approval_status: status },
            );
            await load();
            toast.success(`${kind} ${status}`);
        } catch (e) {
            // Sprint V1 — surface the structured backend error so the
            // operator understands *why* visual approval was refused.
            const detail = e?.response?.data?.detail;
            const code = detail?.error;
            const msg = detail?.message || detail || "Approval update failed";
            if (code === "visual_proof_required") {
                toast.error("Upload or generate a reference image before visual approval/lock.");
            } else if (code === "continuity_asset_not_a_valid_image") {
                toast.error(msg);
            } else {
                toast.error(typeof msg === "string" ? msg : "Approval update failed");
            }
        }
    };

    // Sprint V2 — manual-click AI image generation.
    // - Exactly ONE provider call per click (handled server-side).
    // - First click in the session prompts a credit-confirmation modal
    //   ("This will use AI credits. Generate one AI reference image?").
    // - Subsequent clicks in the same session skip the modal but still
    //   trigger exactly one call each.
    // - V2 acceptance patch (Codex FIX 2): only ONE generation may be
    //   in-flight at a time across ALL cards (character + environment).
    //   Both askGenerate() and runGenerate() bail out with a toast if
    //   `generatingFor` is non-null; all Generate AI Image buttons
    //   stay disabled while a generation is in-flight.
    // - No useEffect generation. No on-approval / on-lock / on-export
    //   generation. No retries. No fallback.
    const runGenerate = useCallback(async (kind, id) => {
        if (generatingFor !== null) {
            toast.error("Another AI image is already generating. Please wait.");
            return;
        }
        setGeneratingFor(id);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/production-continuity/${kind}s/${id}/generate-image`,
            );
            if (data?.ok === true && data?.storage_path) {
                await load();
                toast.success(`AI ${kind} image generated`);
            } else {
                const msg = data?.message
                    || "AI image provider unavailable. Upload a reference image instead.";
                toast.error(msg);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (typeof detail === "string" && detail.includes("not found")) {
                toast.error(detail);
            } else {
                toast.error("Generation failed");
            }
        } finally {
            setGeneratingFor(null);
        }
    }, [projectId, load, generatingFor]);

    const askGenerate = useCallback((kind, id) => {
        if (generatingFor !== null) {
            toast.error("Another AI image is already generating. Please wait.");
            return;
        }
        if (aiCreditsConfirmed) {
            void runGenerate(kind, id);
            return;
        }
        setPendingGenerate({ kind, id });
    }, [aiCreditsConfirmed, runGenerate, generatingFor]);

    const confirmGenerate = useCallback(() => {
        if (!pendingGenerate) return;
        const { kind, id } = pendingGenerate;
        setAiCreditsConfirmed(true);
        setPendingGenerate(null);
        void runGenerate(kind, id);
    }, [pendingGenerate, runGenerate]);

    const cancelGenerate = useCallback(() => {
        setPendingGenerate(null);
    }, []);

    if (loading) {
        return (
            <div className="card-base p-4 text-zinc-500 text-xs" data-testid="continuity-loading">
                Loading Production Continuity…
            </div>
        );
    }

    const chars = lock?.character_bible?.items || [];
    const envs = lock?.environment_bible?.items || [];

    return (
        <div
            className="card-base p-5 space-y-5"
            data-testid="production-continuity-panel"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <ShieldCheck className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Production Continuity</h3>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        Pre-Storyboard Reference Lock
                    </span>
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="btn-secondary text-xs"
                    data-testid="continuity-refresh"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            <div
                className={`border rounded-sm px-3 py-2 text-xs ${
                    readiness?.ready_for_storyboard
                        ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-200"
                        : "border-amber-500/40 bg-amber-500/5 text-amber-200"
                }`}
                data-testid="continuity-readiness-banner"
            >
                <div className="flex items-center gap-2 mb-1">
                    {readiness?.ready_for_storyboard
                        ? <CheckCircle2 className="w-3.5 h-3.5" />
                        : <ShieldAlert className="w-3.5 h-3.5" />}
                    <span className="font-display">
                        {readiness?.ready_for_storyboard
                            ? "Ready for Storyboard"
                            : "Missing continuity references"}
                    </span>
                    <span className="text-[10px] font-mono text-zinc-500 ml-2">
                        characters {readiness?.counts?.characters_approved || 0}/{readiness?.counts?.characters_total || 0}
                        {" · "}
                        environments {readiness?.counts?.environments_approved || 0}/{readiness?.counts?.environments_total || 0}
                        {" · "}
                        reference assets {readiness?.counts?.bound_reference_assets || 0}
                    </span>
                </div>
                {(readiness?.warnings || []).length > 0 && (
                    <ul className="list-disc list-inside text-[11px] opacity-90">
                        {readiness.warnings.map((w, i) => (
                            <li key={i} data-testid={`continuity-warning-${i}`}>{w}</li>
                        ))}
                    </ul>
                )}
                <p className="text-[10px] text-zinc-500 mt-1 italic">
                    Warning-based gate only. You may still run Storyboard /
                    Prompt Pack with continuity missing — the model will be
                    told evidence is incomplete.
                </p>
            </div>

            <section data-testid="continuity-character-section">
                <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-zinc-500" />
                    <h4 className="font-display text-sm">Character Bible</h4>
                    <button
                        type="button"
                        onClick={() => setCharEditor("new")}
                        className="btn-primary text-[11px] ml-auto"
                        data-testid="continuity-character-add"
                    >
                        <Plus className="w-3 h-3" /> Add character
                    </button>
                </div>
                {chars.length === 0 && charEditor !== "new" && (
                    <p className="text-[11px] text-zinc-500 italic">
                        No characters yet. Add at least one approved character before Storyboard.
                    </p>
                )}
                <div className="space-y-2">
                    {chars.map((c) => {
                        const validVisual = hasValidVisual(c.reference_asset_ids);
                        const refIds = c.reference_asset_ids || [];
                        return (
                        <div
                            key={c.id}
                            className="border border-zinc-800 rounded-sm p-2 bg-zinc-950/60"
                            data-testid={`continuity-character-card-${c.id}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-display text-sm">{c.name || "(unnamed)"}</span>
                                <ApprovalPill status={c.approval_status} />
                                <span className="text-[10px] font-mono text-zinc-500">
                                    {refIds.length} refs
                                </span>
                                {!validVisual && (
                                    <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/40 px-1 py-0.5 rounded-sm"
                                          title="Profile is saved as a TEXT-only draft. Visual approval requires an image."
                                          data-testid={`continuity-character-no-visual-chip-${c.id}`}>
                                        no ref image
                                    </span>
                                )}
                                <div className="ml-auto flex gap-1">
                                    {c.approval_status !== "approved" && (
                                        <button
                                            type="button"
                                            onClick={() => approve("character", c.id, "approved")}
                                            disabled={!validVisual}
                                            className={`text-[10px] px-2 py-0.5 ${validVisual ? "btn-primary" : "btn-secondary opacity-50 cursor-not-allowed"}`}
                                            title={validVisual ? "Approve Visual" : "Upload or generate a reference image before visual approval/lock."}
                                            data-testid={`continuity-character-approve-${c.id}`}
                                        >
                                            Approve Visual
                                        </button>
                                    )}
                                    {c.approval_status !== "rejected" && (
                                        <button
                                            type="button"
                                            onClick={() => approve("character", c.id, "rejected")}
                                            className="btn-secondary text-[10px] px-2 py-0.5"
                                            data-testid={`continuity-character-reject-${c.id}`}
                                        >
                                            Reject
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setCharEditor(c)}
                                        className="btn-secondary text-[10px] px-2 py-0.5"
                                        data-testid={`continuity-character-edit-${c.id}`}
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                            <p className="text-[11px] text-zinc-400 line-clamp-2">
                                {c.role && <><span className="text-zinc-500">role:</span> {c.role} · </>}
                                {c.wardrobe && <><span className="text-zinc-500">wardrobe:</span> {c.wardrobe}</>}
                            </p>
                            {refIds.length > 0 && (
                                <div
                                    className="grid grid-cols-6 gap-1 mt-1.5"
                                    data-testid={`continuity-character-thumbs-${c.id}`}
                                >
                                    {refIds.slice(0, 6).map((aid) => {
                                        const a = assetById[aid];
                                        const isImg = a && !a.is_deleted && (
                                            (a.content_type || "").toLowerCase().startsWith("image/")
                                            || (a.asset_type || "").toLowerCase() === "image"
                                            || (a.asset_type || "").toLowerCase() === "logo"
                                        );
                                        return (
                                            <button
                                                type="button"
                                                key={aid}
                                                onClick={(e) => {
                                                    if (!isImg) return;
                                                    e.stopPropagation();
                                                    openAsset({
                                                        assetId: aid,
                                                        title: a?.original_filename || `${c.name || "character"} reference`,
                                                    });
                                                }}
                                                disabled={!isImg}
                                                title={isImg ? "Click to preview full image" : "Reference not available"}
                                                aria-label={isImg ? `Preview ${a?.original_filename || aid}` : "Reference not available"}
                                                className={`relative aspect-square w-full bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden p-0 m-0 transition ${isImg ? "cursor-zoom-in hover:ring-1 hover:ring-emerald-500/40" : "cursor-default"}`}
                                                data-testid={`continuity-character-thumb-${c.id}-${aid}`}
                                            >
                                                {isImg ? (
                                                    <img
                                                        src={assetThumbUrl(a)}
                                                        alt={a?.original_filename || aid}
                                                        className="w-full h-full object-contain"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <ImageIcon className="w-3 h-3 text-zinc-700" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {!validVisual && (
                                <p
                                    className="text-[10px] text-zinc-500 italic mt-1"
                                    data-testid={`continuity-character-text-only-notice-${c.id}`}
                                >
                                    Profile is saved as a TEXT-only draft. Visual approval requires an image.
                                </p>
                            )}
                            {/* Sprint V2 — AI Generated Reference section. Clearly
                                labelled, NOT mixed with uploaded thumbnails above.
                                V2 acceptance patch (Codex FIX 1): render ONLY when
                                BOTH the asset id AND storage_path exist, and pass
                                `id` to assetThumbUrl so the stream URL is valid. */}
                            {c.generated_image_asset_id
                              && c.generated_image_storage_path && (
                                <div
                                    className="mt-1.5 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-1.5"
                                    data-testid={`continuity-character-ai-generated-section-${c.id}`}
                                >
                                    <div className="flex items-center gap-1 mb-1">
                                        <span
                                            className="text-[9px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-1 py-0.5 rounded-sm"
                                            data-testid={`continuity-character-ai-generated-badge-${c.id}`}
                                        >
                                            AI Generated Reference
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openAsset({
                                                assetId: c.generated_image_asset_id,
                                                title: `AI generated reference · ${c.name || c.id}`,
                                            });
                                        }}
                                        title="Click to preview full image"
                                        aria-label={`Preview AI generated reference for ${c.name || c.id}`}
                                        data-testid={`continuity-character-ai-generated-open-${c.id}`}
                                        className="w-full p-0 m-0 border-0 bg-transparent cursor-zoom-in rounded-sm overflow-hidden hover:ring-1 hover:ring-emerald-500/40 transition"
                                    >
                                        <img
                                            src={assetThumbUrl({
                                                id: c.generated_image_asset_id,
                                                storage_path: c.generated_image_storage_path,
                                                content_type: "image/png",
                                            })}
                                            alt={`AI generated reference for ${c.name || c.id}`}
                                            className="w-full h-32 object-contain rounded-sm bg-zinc-950"
                                            data-testid={`continuity-character-ai-generated-img-${c.id}`}
                                            loading="lazy"
                                        />
                                    </button>
                                </div>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                                <button
                                    type="button"
                                    onClick={() => askGenerate("character", c.id)}
                                    disabled={generatingFor !== null}
                                    className={`text-[10px] px-2 py-0.5 btn-secondary ${generatingFor !== null ? "opacity-50 cursor-wait" : ""}`}
                                    title="Manual click only. Uses AI credits."
                                    data-testid={`continuity-character-generate-${c.id}`}
                                >
                                    {generatingFor === c.id ? "Generating…" : "Generate AI Image"}
                                </button>
                                <span className="text-[10px] text-zinc-500 italic">
                                    Uses AI credits.
                                </span>
                            </div>
                        </div>
                        );
                    })}
                </div>
                {charEditor && (
                    <div className="mt-2">
                        <CharacterEditor
                            projectId={projectId}
                            initial={charEditor === "new" ? {} : charEditor}
                            onSaved={async () => { setCharEditor(null); await load(); }}
                            onCancel={() => setCharEditor(null)}
                            onOpenAsset={openAsset}
                        />
                    </div>
                )}
            </section>

            <section data-testid="continuity-environment-section">
                <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-zinc-500" />
                    <h4 className="font-display text-sm">Environment Bible</h4>
                    <button
                        type="button"
                        onClick={() => setEnvEditor("new")}
                        className="btn-primary text-[11px] ml-auto"
                        data-testid="continuity-environment-add"
                    >
                        <Plus className="w-3 h-3" /> Add environment
                    </button>
                </div>
                {envs.length === 0 && envEditor !== "new" && (
                    <p className="text-[11px] text-zinc-500 italic">
                        No environments yet. Add at least one approved environment before Storyboard.
                    </p>
                )}
                <div className="space-y-2">
                    {envs.map((e) => {
                        const validVisual = hasValidVisual(e.reference_asset_ids);
                        const refIds = e.reference_asset_ids || [];
                        return (
                        <div
                            key={e.id}
                            className="border border-zinc-800 rounded-sm p-2 bg-zinc-950/60"
                            data-testid={`continuity-environment-card-${e.id}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-display text-sm">{e.name || "(unnamed)"}</span>
                                <ApprovalPill status={e.approval_status} />
                                <span className="text-[10px] font-mono text-zinc-500">
                                    {refIds.length} refs
                                </span>
                                {!validVisual && (
                                    <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/40 px-1 py-0.5 rounded-sm"
                                          title="Profile is saved as a TEXT-only draft. Visual approval requires an image."
                                          data-testid={`continuity-environment-no-visual-chip-${e.id}`}>
                                        no ref image
                                    </span>
                                )}
                                <div className="ml-auto flex gap-1">
                                    {e.approval_status !== "approved" && (
                                        <button
                                            type="button"
                                            onClick={() => approve("environment", e.id, "approved")}
                                            disabled={!validVisual}
                                            className={`text-[10px] px-2 py-0.5 ${validVisual ? "btn-primary" : "btn-secondary opacity-50 cursor-not-allowed"}`}
                                            title={validVisual ? "Approve Visual" : "Upload or generate a reference image before visual approval/lock."}
                                            data-testid={`continuity-environment-approve-${e.id}`}
                                        >
                                            Approve Visual
                                        </button>
                                    )}
                                    {e.approval_status !== "rejected" && (
                                        <button
                                            type="button"
                                            onClick={() => approve("environment", e.id, "rejected")}
                                            className="btn-secondary text-[10px] px-2 py-0.5"
                                            data-testid={`continuity-environment-reject-${e.id}`}
                                        >
                                            Reject
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setEnvEditor(e)}
                                        className="btn-secondary text-[10px] px-2 py-0.5"
                                        data-testid={`continuity-environment-edit-${e.id}`}
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                            <p className="text-[11px] text-zinc-400 line-clamp-2">
                                {e.location_type && <><span className="text-zinc-500">type:</span> {e.location_type} · </>}
                                {e.visual_mood && <><span className="text-zinc-500">mood:</span> {e.visual_mood}</>}
                            </p>
                            {refIds.length > 0 && (
                                <div
                                    className="grid grid-cols-6 gap-1 mt-1.5"
                                    data-testid={`continuity-environment-thumbs-${e.id}`}
                                >
                                    {refIds.slice(0, 6).map((aid) => {
                                        const a = assetById[aid];
                                        const isImg = a && !a.is_deleted && (
                                            (a.content_type || "").toLowerCase().startsWith("image/")
                                            || (a.asset_type || "").toLowerCase() === "image"
                                            || (a.asset_type || "").toLowerCase() === "logo"
                                        );
                                        return (
                                            <button
                                                type="button"
                                                key={aid}
                                                onClick={(ev) => {
                                                    if (!isImg) return;
                                                    ev.stopPropagation();
                                                    openAsset({
                                                        assetId: aid,
                                                        title: a?.original_filename || `${e.name || "environment"} reference`,
                                                    });
                                                }}
                                                disabled={!isImg}
                                                title={isImg ? "Click to preview full image" : "Reference not available"}
                                                aria-label={isImg ? `Preview ${a?.original_filename || aid}` : "Reference not available"}
                                                className={`relative aspect-square w-full bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden p-0 m-0 transition ${isImg ? "cursor-zoom-in hover:ring-1 hover:ring-emerald-500/40" : "cursor-default"}`}
                                                data-testid={`continuity-environment-thumb-${e.id}-${aid}`}
                                            >
                                                {isImg ? (
                                                    <img
                                                        src={assetThumbUrl(a)}
                                                        alt={a?.original_filename || aid}
                                                        className="w-full h-full object-contain"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <ImageIcon className="w-3 h-3 text-zinc-700" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {!validVisual && (
                                <p
                                    className="text-[10px] text-zinc-500 italic mt-1"
                                    data-testid={`continuity-environment-text-only-notice-${e.id}`}
                                >
                                    Profile is saved as a TEXT-only draft. Visual approval requires an image.
                                </p>
                            )}
                            {/* Sprint V2 — AI Generated Reference section.
                                Clearly labelled, separate from uploaded thumbs.
                                V2 acceptance patch (Codex FIX 1): render ONLY
                                when BOTH the asset id AND storage_path exist,
                                and pass `id` to assetThumbUrl so the stream
                                URL is valid (avoids /api/assets/undefined/stream). */}
                            {e.generated_image_asset_id
                              && e.generated_image_storage_path && (
                                <div
                                    className="mt-1.5 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-1.5"
                                    data-testid={`continuity-environment-ai-generated-section-${e.id}`}
                                >
                                    <div className="flex items-center gap-1 mb-1">
                                        <span
                                            className="text-[9px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 px-1 py-0.5 rounded-sm"
                                            data-testid={`continuity-environment-ai-generated-badge-${e.id}`}
                                        >
                                            AI Generated Reference
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            openAsset({
                                                assetId: e.generated_image_asset_id,
                                                title: `AI generated reference · ${e.name || e.id}`,
                                            });
                                        }}
                                        title="Click to preview full image"
                                        aria-label={`Preview AI generated reference for ${e.name || e.id}`}
                                        data-testid={`continuity-environment-ai-generated-open-${e.id}`}
                                        className="w-full p-0 m-0 border-0 bg-transparent cursor-zoom-in rounded-sm overflow-hidden hover:ring-1 hover:ring-emerald-500/40 transition"
                                    >
                                        <img
                                            src={assetThumbUrl({
                                                id: e.generated_image_asset_id,
                                                storage_path: e.generated_image_storage_path,
                                                content_type: "image/png",
                                            })}
                                            alt={`AI generated reference for ${e.name || e.id}`}
                                            className="w-full h-32 object-contain rounded-sm bg-zinc-950"
                                            data-testid={`continuity-environment-ai-generated-img-${e.id}`}
                                            loading="lazy"
                                        />
                                    </button>
                                </div>
                            )}
                            <div className="flex items-center gap-2 mt-1.5">
                                <button
                                    type="button"
                                    onClick={() => askGenerate("environment", e.id)}
                                    disabled={generatingFor !== null}
                                    className={`text-[10px] px-2 py-0.5 btn-secondary ${generatingFor !== null ? "opacity-50 cursor-wait" : ""}`}
                                    title="Manual click only. Uses AI credits."
                                    data-testid={`continuity-environment-generate-${e.id}`}
                                >
                                    {generatingFor === e.id ? "Generating…" : "Generate AI Image"}
                                </button>
                                <span className="text-[10px] text-zinc-500 italic">
                                    Uses AI credits.
                                </span>
                            </div>
                        </div>
                        );
                    })}
                </div>
                {envEditor && (
                    <div className="mt-2">
                        <EnvironmentEditor
                            projectId={projectId}
                            initial={envEditor === "new" ? {} : envEditor}
                            onSaved={async () => { setEnvEditor(null); await load(); }}
                            onCancel={() => setEnvEditor(null)}
                            onOpenAsset={openAsset}
                        />
                    </div>
                )}
            </section>

            <section>
                <ReferenceLockBinder
                    projectId={projectId}
                    lock={lock}
                    onChange={load}
                    onOpenAsset={openAsset}
                />
            </section>

            <div
                className="text-[10px] text-zinc-500 italic border-t border-zinc-800 pt-2"
                data-testid="continuity-ai-gen-notice"
            >
                AI reference image generation is a per-card MANUAL click.
                Each click runs exactly one AI provider call and uses credits.
            </div>

            {/* Sprint V2 — once-per-session credit-confirmation modal. */}
            {pendingGenerate && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
                    data-testid="continuity-ai-confirm-modal"
                >
                    <div className="bg-zinc-950 border border-zinc-800 rounded-md max-w-sm w-full p-4 space-y-3">
                        <h3 className="font-display text-base">
                            This will use AI credits
                        </h3>
                        <p className="text-sm text-zinc-300">
                            Generate one AI reference image for this {pendingGenerate.kind}?
                            One click runs exactly one AI provider call.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={cancelGenerate}
                                className="btn-secondary text-sm px-3 py-1"
                                data-testid="continuity-ai-confirm-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmGenerate}
                                className="btn-primary text-sm px-3 py-1"
                                data-testid="continuity-ai-confirm-ok"
                            >
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Reference Image Preview / Lightbox Fix — single shared modal. */}
            <ImageLightbox
                open={lightbox.open}
                assetId={lightbox.assetId}
                title={lightbox.title}
                onClose={closeLightbox}
            />
        </div>
    );
}
